import type { ReactNode, Ref, SyntheticEvent } from 'react'
import type { ResizeHandleAxis } from 'react-resizable'
import * as S from './ClassicResizeControls.styled'
import { useRecoilValue } from 'recoil'
import { itemsLockedAtom } from 'atoms'
import { ClassicResizeHandleElement } from 'components/page/Items/ClassicResizeHandle'
import { useItemResize } from '@/hooks/editor/useItemResize'

type Controlled<T> = [T, (value: T) => void]

type ResizeCallbackData = {
  size: { width: number; height: number }
}

type ClassicResizeControlsProps = {
  x?: number
  y?: number
  z?: number
  width?: number | string
  height?: number | string
  rotate?: number
  internalWidth?: Controlled<number>
  internalHeight?: Controlled<number>
  type?: string
  selected?: boolean
  disabled?: boolean
  resizingState?: Controlled<string>
  children?: ReactNode
  id?: string
}

/**
 * The resize gesture of a classic (TinyMCE-era) item — text, image, line,
 * QR code, watermark — identical to staging's: one south-east handle,
 * pointer travel applied to the frame unscaled, the released size committed
 * as the library reports it. The classic items change nothing until they
 * are replaced by editor text boxes. An editor text box or rule resizes
 * through `ResizeControls.tsx`.
 */
export const ClassicResizeControls = ({
  x,
  y,
  z,
  width,
  height,
  rotate,
  internalWidth: [internalWidth, setInternalWidth] = [0, () => {}],
  internalHeight: [internalHeight, setInternalHeight] = [0, () => {}],
  type,
  selected,
  disabled,
  resizingState: [, setResizingState] = ['idle', () => {}],
  children,
  id,
}: ClassicResizeControlsProps) => {
  const itemLocked = useRecoilValue(itemsLockedAtom)

  // Use shared resize hook for committing size changes
  const { setSize } = useItemResize(id || '')

  const handlePlacements: ResizeHandleAxis[] = ['se']

  const minConstraints: [number, number] = type === 'line' ? [2, 2] : [20, 20]
  const axis = type === 'line' ? 'x' : 'both'

  const onResizeStart = () => {
    setResizingState('start')
  }

  const onResize = (
    _event: SyntheticEvent,
    { size: { width, height } }: ResizeCallbackData,
  ) => {
    setInternalWidth(width)
    setInternalHeight(height)
    setResizingState('resizing')
  }

  const onResizeStop = (
    _event: SyntheticEvent,
    { size: { width, height } }: ResizeCallbackData,
  ) => {
    // Use shared hook to commit both dimensions and update imageEdits.targetSize
    setSize(width, height)
    setResizingState('stop')
    setTimeout(() => setResizingState('idle'), 0)
  }

  const lockedAspectRatioItem = [
    'image',
    'watermark',
    'designAsset',
    'qrCode',
  ].includes(type as string)

  return (
    <S.ClassicResizeControls
      height={internalHeight || 0}
      width={internalWidth || 0}
      rotate={rotate}
      onResizeStart={onResizeStart}
      onResize={onResize}
      onResizeStop={onResizeStop}
      minConstraints={minConstraints}
      selected={selected}
      resizeHandles={handlePlacements}
      lockAspectRatio={lockedAspectRatioItem}
      axis={axis}
      disabled={disabled}
      handle={(position: ResizeHandleAxis, ref: Ref<HTMLDivElement>) => {
        return (
          <ClassicResizeHandleElement
            innerRef={ref}
            itemLocked={itemLocked}
            x={x}
            y={y}
            z={z}
            height={height}
            width={width}
            position={position}
            selected={selected}
            itemId={id}
          />
        )
      }}
    >
      {children}
    </S.ClassicResizeControls>
  )
}

export default ClassicResizeControls
